import { Search as SearchIcon } from '@insify/design-system/icons';
import { useDebouncedCallback } from '@insify/react-hooks';
import { useRef, useState } from 'react';

import { InputAutocomplete } from '../InputAutocomplete';

import * as styles from './AddressAutocomplete.module.css';

import type {
  Address,
  AutocompleteResult,
  RequestAddressDetail,
  RequestAutocomplete,
} from '@insify/frontend-google-maps';
import type React from 'react';

export type FormatAutocompleteSuggestion = (address: AutocompleteResult) => string;

type PropsAddressAutocomplete = {
  requestAutocomplete: RequestAutocomplete;
  requestAddressDetail: RequestAddressDetail;
  formatAutoCompleteSuggestion: FormatAutocompleteSuggestion;
  onSelect: (address: Partial<Address>) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  label?: string;
  placeholder?: string;
  error?: string;
  positionSuggestions?: 'top' | 'bottom';
  areFieldsVisible?: boolean;
  fields: React.ReactNode;
};

export const AddressAutocomplete = (props: PropsAddressAutocomplete) => {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([]);

  const autoCompletedValue = useRef('');
  const inputRef = useRef<HTMLInputElement>(null);

  const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target?.value !== autoCompletedValue.current) {
      props.onChange?.(e);
      const query = e.target?.value;
      querySuggestions(query);
    }
  };

  const onSelect = async (addressPartial: AutocompleteResult) => {
    autoCompletedValue.current = props.formatAutoCompleteSuggestion(addressPartial);
    if (inputRef.current?.value) {
      inputRef.current.value = '';
    }
    const address = await props.requestAddressDetail(addressPartial);
    props.onSelect(address);
  };

  const querySuggestions = useDebouncedCallback(async (query: string) => {
    if (query?.length >= 2) {
      setIsLoading(true);
      const data = await props.requestAutocomplete(query, 5);
      setSuggestions(data);
    } else {
      setSuggestions([]);
    }
    setIsLoading(false);
  }, 300);

  return (
    <>
      <InputAutocomplete<AutocompleteResult>
        label={props.label}
        placeholder={props.placeholder}
        error={props.error}
        positionSuggestions={props.positionSuggestions}
        trackingId='autocomplete'
        id='autocomplete'
        name='_addressAutoComplete'
        inputRef={inputRef}
        options={suggestions}
        onChange={onSearch}
        onSelect={onSelect}
        mapOptionToSuggestionLabel={props.formatAutoCompleteSuggestion}
        mapOptionToKey={props.formatAutoCompleteSuggestion}
        before={<SearchIcon />}
        loading={isLoading}
        autoComplete='autocomplete-address'
      />
      <div style={{ height: props.areFieldsVisible ? 'auto' : '0px', overflow: 'hidden' }}>
        <div className={styles.background}>{props.fields}</div>
        <div className={styles.fieldPadding} />
      </div>
    </>
  );
};
